// src/pages/Home/sections/SubscribeConfirmPage.jsx
import React, { useEffect, useState } from "react";
import { CheckCircle, XCircle, Loader2, Mail } from "lucide-react";
import { useSearchParams, Link } from "react-router-dom";
import { api, API_ENDPOINTS } from "../../../api";

const SubscribeConfirmPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [status, setStatus] = useState("loading"); // "loading" | "success" | "error" | "missing"
  const [email, setEmail] = useState(null);

  useEffect(() => {
    if (!token) {
      setStatus("missing");
      return;
    }

    const confirm = async () => {
      try {
        const data = await api.get(
          `${API_ENDPOINTS.NEWSLETTER_CONFIRM}?token=${encodeURIComponent(token)}`
        );

        if (data.success) {
          setEmail(data.email);
          setStatus("success");
        } else {
          setStatus("error");
        }
      } catch {
        setStatus("error");
      }
    };

    confirm();
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-16">
      <div className="bg-surface rounded-2xl border border-surface-border shadow-card w-full max-w-md p-8 text-center">

        <div className="flex justify-center mb-6">
          {status === "loading" && (
            <Loader2 className="w-16 h-16 text-primary animate-spin" />
          )}
          {status === "success" && (
            <div className="bg-primary/10 rounded-full p-4">
              <CheckCircle className="w-12 h-12 text-primary" />
            </div>
          )}
          {(status === "error" || status === "missing") && (
            <div className="bg-red-500/10 rounded-full p-4">
              <XCircle className="w-12 h-12 text-red-500" />
            </div>
          )}
        </div>

        {status === "loading" && (
          <>
            <h1 className="font-heading font-bold text-2xl text-text mb-2">
              Confirming your subscription…
            </h1>
            <p className="font-body text-text-muted text-sm">
              Please wait while we verify your email address.
            </p>
          </>
        )}

        {status === "success" && (
          <>
            <h1 className="font-heading font-bold text-2xl text-text mb-3">
              You're subscribed!
            </h1>
            {email && (
              <div className="flex justify-center mb-4">
                <div className="flex items-center gap-2 bg-primary/10 text-primary text-sm font-semibold px-4 py-2 rounded-full">
                  <Mail className="w-4 h-4" />
                  {email}
                </div>
              </div>
            )}
            <p className="font-body text-text-muted text-sm mb-6">
              You'll now receive our latest deals and travel tips straight to
              your inbox.
            </p>
            <Link
              to="/"
              className="inline-block w-full bg-primary hover:bg-primary-hover text-white font-heading font-bold text-sm uppercase tracking-wider py-4 rounded-xl transition-colors"
            >
              Find a rental deal
            </Link>
          </>
        )}

        {status === "error" && (
          <>
            <h1 className="font-heading font-bold text-2xl text-text mb-3">
              Confirmation failed
            </h1>
            <p className="font-body text-text-muted text-sm mb-6">
              This confirmation link is invalid or has expired. Please
              re-subscribe on our homepage to receive a new link.
            </p>
            <Link
              to="/"
              className="inline-block w-full bg-primary hover:bg-primary-hover text-white font-heading font-bold text-sm uppercase tracking-wider py-4 rounded-xl transition-colors"
            >
              Back to homepage
            </Link>
          </>
        )}

        {status === "missing" && (
          <>
            <h1 className="font-heading font-bold text-2xl text-text mb-3">
              Invalid link
            </h1>
            <p className="font-body text-text-muted text-sm mb-6">
              No confirmation token was found in this link. Please use the
              exact link from your confirmation email.
            </p>
            <Link
              to="/"
              className="inline-block w-full bg-primary hover:bg-primary-hover text-white font-heading font-bold text-sm uppercase tracking-wider py-4 rounded-xl transition-colors"
            >
              Back to homepage
            </Link>
          </>
        )}

        {status !== "loading" && (
          <p className="font-body text-text-muted text-xs mt-6">
            Need help?{" "}
            <Link to="/support" className="text-primary hover:underline">
              Contact us
            </Link>
          </p>
        )}
      </div>
    </div>
  );
};

export default SubscribeConfirmPage;
